import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
} from "react";

import { fetchAddressList, updateStoredJobDetails } from "../../service";
import { theme } from "../../../../theme";
import EditAddress from "./EditAddress";

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_DELAY = 300;

const AddressSearch = ({
    address,
    setAddress,
    jobGeolocationDetails,
    handleUpdateAddress,
    formEditAddressRef = null,
    setJobGeolocationDetails,
    isCheckingPrice = false,
}) => {
    const [query, setQuery] = useState("");
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showEditAddress, setShowEditAddress] = useState(false);
    const [debouncedQuery, setDebouncedQuery] = useState("");

    // Debounce Query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(query.trim());
        }, DEBOUNCE_DELAY);

        return () => clearTimeout(timer);
    }, [query]);

    // Fetch Address List
    useEffect(() => {
        if (address || debouncedQuery.length < MIN_SEARCH_LENGTH) {
            setAddresses([]);
            setLoading(false);
            return;
        }

        let active = true;

        const getAddressList = async () => {
            try {
                setLoading(true);
                const response = await fetchAddressList(debouncedQuery);

                if (!active) return;
                setAddresses(
                    response?.status === "Success" ? response?.list || [] : []
                );
            } catch (error) {
                if (active) {
                    setAddresses([]);
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        };

        getAddressList();

        return () => {
            active = false;
        };
    }, [debouncedQuery, address]);

    const handleShowEditAddress = () => setShowEditAddress(true);
    const handleHideEditAddress = () => setShowEditAddress(false);

    // Input Change
    const handleChange = useCallback((e) => {
        const value = e.target.value;

        setQuery(value);

        if (address) {
            setAddress(null);
        }
    }, [address, setAddress]);

    // Select Address
    const handleSelect = useCallback((item) => {
        const data = {
            value: item,
            label: `${item?.StreetAddress}, ${item?.Place}`,
        };

        setAddress?.(data);

        updateStoredJobDetails("address", data);

        setAddresses([]);
    }, [setAddress]);

    // Clear Selected Address
    const handleClear = useCallback(() => {
        setAddress(null);
        setQuery("");
        setAddresses([]);
        handleHideEditAddress();
        setJobGeolocationDetails(null);
    }, [setAddress, setJobGeolocationDetails]);

    const showNoResults = useMemo(() => {
        return (
            !loading &&
            debouncedQuery &&
            addresses.length === 0 &&
            !address
        );
    }, [loading, debouncedQuery, addresses.length, address]);

    return (
        <div style={styles.container}>
            {/* Search Input */}
            {!address && (
                <div style={styles.searchBox}>
                    <i className={`material-symbols-rounded ms-1 me-2 text-${theme.main}`}>
                        search
                    </i>

                    <input
                        value={query}
                        onChange={handleChange}
                        placeholder="Enter postcode"
                        style={styles.input}
                    />
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div style={styles.info}>
                    Loading...
                </div>
            )}

            {/* Address List */}
            {!loading && !address && addresses.length > 0 && (
                <div style={styles.listContainer(isCheckingPrice)}>
                    {addresses.map((item) => (
                        <div
                            key={item.Id}
                            style={styles.card}
                            onClick={() => handleSelect(item)}
                        >
                            <div style={styles.row}>
                                <span style={styles.locationIcon}>
                                    <i className="material-symbols-rounded">
                                        location_on
                                    </i>
                                </span>

                                <div>
                                    <div style={styles.title}>
                                        {item.StreetAddress}
                                    </div>

                                    <div style={styles.subtitle}>
                                        {item.Place}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Empty Result */}
            {showNoResults && (
                <div style={styles.info}>
                    No addresses found
                </div>
            )}

            {/* Selected Address */}
            {!loading && address && (
                <>
                    <div style={styles.card}>
                        <div style={styles.row}>
                            <span style={styles.locationIcon}>
                                <i className="material-symbols-rounded text-danger">
                                    location_on
                                </i>
                            </span>

                            <div style={{ flex: 1 }}>
                                <div style={styles.addressHeader}>
                                    <div style={styles.title}>
                                        {address?.value?.StreetAddress}
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleClear}
                                        style={styles.resetButton}
                                    >
                                        <i className="material-symbols-rounded">
                                            restart_alt
                                        </i>
                                    </button>
                                </div>

                                <div style={styles.subtitle}>
                                    {address?.value?.Place}
                                </div>
                            </div>
                        </div>
                    </div>

                    {!showEditAddress && (
                        <p className="mt-3 text-center mb-0">
                            <button
                                type="button"
                                className="btn btn-link text-secondary mb-2"
                                onClick={handleShowEditAddress}
                            >
                                Edit address
                            </button>
                        </p>
                    )}
                </>
            )}

            {/* Edit Address */}
            <EditAddress
                ref={formEditAddressRef}
                jobGeolocationDetails={jobGeolocationDetails}
                onUpdateAddress={handleUpdateAddress}
                showModalView={false}
                showEditAddress={showEditAddress}
                handleShowEditAddress={handleShowEditAddress}
            />
        </div>
    );
};

const styles = {
    container: {
        maxWidth: 420,
        margin: "20px auto",
        fontFamily: "Arial, sans-serif",
    },

    searchBox: {
        display: "flex",
        alignItems: "center",
        border: "2px solid #6bd19f",
        borderRadius: 12,
        padding: "5px 15px",
        marginBottom: 15,
        background: "#fff",
    },

    input: {
        border: "none",
        outline: "none",
        width: "100%",
        fontSize: 15,
        background: "transparent",
    },

    listContainer: (isCheckingPrice) => ({
        height: isCheckingPrice ? 535 : 510,
        overflowY: "auto",
    }),

    card: {
        background: "#f2f2f2",
        padding: 10,
        borderRadius: 12,
        marginBottom: 10,
        cursor: "pointer",
    },

    row: {
        display: "flex",
        gap: 10,
        alignItems: "center",
    },

    locationIcon: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
    },

    title: {
        fontWeight: 600,
        fontSize: 15,
    },

    subtitle: {
        fontSize: 13,
        color: "#666",
        marginTop: 2,
    },

    info: {
        textAlign: "center",
        color: "#888",
        marginTop: 20,
    },

    addressHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
    },

    resetButton: {
        border: "none",
        background: "#ffe5e5",
        color: "#dc3545",
        width: 32,
        height: 32,
        borderRadius: "50%",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
};

export default React.memo(AddressSearch);